import React from 'react';
import { AsyncLocationlessDialog } from '@atlassian/jira-board-location-dialogs-locationless/src/async';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants';
import { usePathParam } from '@atlassian/jira-platform-router-utils';
import RedirectIfProjectArchived from '@atlassian/jira-redirect-archived-project';
import {
	useResource,
	useRouterActions,
	useQueryParam,
	type RouteResourceResponse,
} from '@atlassian/jira-router';
import { uifBoardResource } from '@atlassian/jira-router-resources-uif-board';
import { useEcClient } from '@atlassian/jira-software-ec-client';
import type { UIFBoardCachedDataResult } from '@atlassian/jira-software-uif-early-script';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product';
import { Breadcrumbs } from '@atlassian/jira-spa-apps-software-breadcrumbs';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

import { ERROR_MESSAGE_BOARD_IS_LOCATIONLESS } from '../../../model/constants';
import { renderBoardApp } from '../board-app';
import { NoSoftwareLicense } from '../no-software-license';

export const BoardWithRouterCMP = ({ hasSoftwareLicense }: { hasSoftwareLicense: boolean }) => {
	const [paramBoardId] = usePathParam('boardId');
	const [projectKey] = usePathParam('projectKey');
	const cmpBoardData: RouteResourceResponse<UIFBoardCachedDataResult | null> =
		useResource<UIFBoardCachedDataResult | null>(uifBoardResource);

	const tenantContext = useTenantContext();
	const boardId = Number(paramBoardId);

	const { loading, data, error } = cmpBoardData;
	const boardLocation = data?.result?.boardLocation;
	const { key, id, name } = boardLocation ?? {};

	const projectDetails = { key, id, name };
	const projectKeyFromContext = projectDetails.key || projectKey || '';

	const history = useRouterActions();

	// for locationless boards with old style links like: https://domain.com/secure/RapidBoard.jspa?rapidView=10&useStoredSettings=true&locationless=true
	const [rapidViewBoardIdString] = useQueryParam('rapidView');
	const rapidViewBoardId = Number(rapidViewBoardIdString);
	const [locationlessFromRapidBoardLink] = useQueryParam('locationless');
	const isLocationlessRapidBoardLink =
		rapidViewBoardId && locationlessFromRapidBoardLink === 'true';

	const { saveCardMutation } = useEcClient({
		analyticsMetadata: { scenario: 'board', isCompanyManaged: true },
	});

	const isCacheHit = Boolean(data?.isCacheHit);

	const renderBoard = () => (
		<>
			<Breadcrumbs />
			<SubProductUpdater subProduct="software" />

			{renderBoardApp({
				rapidViewId: boardId,
				projectKey: projectKeyFromContext,
				projectType: SOFTWARE_PROJECT,
				context: { tenantContext },
				cmpBoardData,
				boardData: null,
				history,
				isCacheHit,
				isCMPBoard: true,
				onCardsChange: saveCardMutation,
			})}

			<RedirectIfProjectArchived projectKey={projectKey || projectKeyFromContext || null} />
		</>
	);

	if (hasSoftwareLicense) {
		if (!loading && error && error.message === ERROR_MESSAGE_BOARD_IS_LOCATIONLESS) {
			return <AsyncLocationlessDialog rapidViewId={boardId} />;
		}

		if (isLocationlessRapidBoardLink) {
			return (
				<AsyncLocationlessDialog
					rapidViewId={rapidViewBoardId}
					classicLocationless={isLocationlessRapidBoardLink}
				/>
			);
		}
		return renderBoard();
	}
	return <NoSoftwareLicense />;
};
